import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { CATALOG } from '@center-platform/component-catalog';
import {
  useMediaQuery,
  MessageAtom,
  ComposerShellOrganism,
  createComposerState,
  type SelectedNode,
} from 'design-system/my-lib';
import { PREVIEW_ROOT_ID, buildCatalogPreviewDocument } from './catalogPreviewDocument';
import './ComposeTargetPage.scss';

/**
 * `/compose/composition/:name` — the code-backed-lock half of the composer shell (EAINT-844
 * Phase F1, Task 3). A code-backed `CATALOG` entry has no draft/version/save machinery — it is
 * immutable, shipped-with-the-platform vocabulary — so this route never wires an edit path into
 * it. `:name` that resolves to a real, org-owned composition instead of a catalog type has no
 * composer-shell target yet (OQ-8: threading `PUT /compositions/:name/versions/:v` and wrapping
 * a bare a2ui subtree in a synthetic `SpaceDocument` for canvas preview is real design work the
 * spec never scoped) — that case gets an honest "not yet supported, use the Explorer" message
 * rather than a fabricated editing surface.
 */
const ComposeCompositionPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const belowDesktop = useMediaQuery('(max-width: 1023px)');

  const catalogEntry = useMemo(() => CATALOG.find((entry) => entry.type === name), [name]);

  const composerState = useMemo(() => {
    if (!catalogEntry) return null;
    return createComposerState(buildCatalogPreviewDocument(catalogEntry.type));
  }, [catalogEntry]);

  const selectedNode = useMemo<SelectedNode | null>(() => {
    if (!catalogEntry) return null;
    return {
      componentId: PREVIEW_ROOT_ID,
      componentType: catalogEntry.type,
      catalogEntry,
      compositionInterface: { props: catalogEntry.props, slots: [], emits: [] },
    };
  }, [catalogEntry]);

  // Mirrors `ComposeTargetPage`'s `composer_opened` so every composer entry point is measurable
  // under one event. The unsupported branch gets its own event: it is the only signal that says
  // how often a real composition is opened with no canvas to open it into (OQ-8).
  useEffect(() => {
    if (belowDesktop || !name) return;
    if (catalogEntry) {
      logAnalyticsEvent('composer_opened', undefined, {
        targetKind: 'catalog',
        scopeKind: 'org',
        locked: true,
      });
      return;
    }
    logAnalyticsEvent('composer_composition_target_unsupported', undefined, { systemName: name });
  }, [belowDesktop, name, catalogEntry, logAnalyticsEvent]);

  const handleBack = () => navigate('/studio/space');
  const handleOpenInExplorer = () => navigate(`/studio/space?openEntry=${encodeURIComponent(name ?? '')}`);

  if (belowDesktop) {
    return (
      <div className="compose-target-page compose-target-page--desktop-only">
        <MessageAtom severity="info" text={t('composer.page.desktopOnlyTitle')} />
        <p className="compose-target-page__desktop-only-description">{t('composer.page.desktopOnlyDescription')}</p>
      </div>
    );
  }

  if (!catalogEntry || !composerState) {
    return (
      <div className="compose-target-page compose-target-page--desktop-only">
        <MessageAtom severity="info" text={t('composer.page.compositionTargetUnsupported')} />
        <button
          type="button"
          className="compose-target-page__open-in-explorer-link"
          data-testid="compose-composition-open-in-explorer"
          onClick={handleOpenInExplorer}
        >
          {t('composer.explorer.tabLabel')}
        </button>
      </div>
    );
  }

  return (
    <div className="compose-target-page">
      <ComposerShellOrganism
        state={composerState}
        title={catalogEntry.type}
        selectedNode={selectedNode}
        onSelect={() => undefined}
        onBack={handleBack}
        locked
      />
    </div>
  );
};

export default ComposeCompositionPage;
